import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  LayoutContextProvider,
  LiveKitRoom,
  RoomAudioRenderer,
  useCreateLayoutContext,
  usePersistentUserChoices,
} from '@livekit/components-react'
import { useMediaQuery } from 'usehooks-ts'

import { useNavigation } from 'modules/core/hooks/useNavigation'

import { Chat } from 'modules/meetings/components/Chat'
import { ControlBar } from 'modules/meetings/components/ControlBar'
import { Drawer } from 'modules/meetings/components/Drawer'
import { MeetingErrorLayout } from 'modules/meetings/components/Layout/MeetingErrorLayout'
import { RoomHeader } from 'modules/meetings/components/RoomHeader'
import { SideBar } from 'modules/meetings/components/SideBar'
import { RoomSkeleton } from 'modules/meetings/components/Skeletons/RoomSkeleton'
import { VideoGrid } from 'modules/meetings/components/VideoGrid'
import { RoomProvider } from 'modules/meetings/lib/context/RoomContext/RoomProvider'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useRoom } from 'modules/meetings/lib/hooks/useRoom'

const SIDEBAR_WIDTH = 612

const MeetingRoomPageContent = () => {
  const { employeeId } = useParams<{ employeeId: string }>()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { loading, room, error, isChatOpen, isSideBarOpen } = useRoom()
  const { loading: pageDetailsLoading, meetingId } = useMeeting()
  const layoutContext = useCreateLayoutContext()
  const { userChoices } = usePersistentUserChoices()
  const isMobile = useMediaQuery('(max-width: 1024px)')

  const handleDisconnect = () => {
    navigate(
      navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
        employeeId: employeeId ?? '',
        meetingId: meetingId ?? '',
      })
    )
  }

  if (!loading && error) {
    return <MeetingErrorLayout />
  }

  const portalContent = (
    <Box
      position="fixed"
      width="full"
      height="full"
      style={{
        zIndex: '999',
        top: '0',
        left: '0',
      }}
      background="grey000White"
    >
      {loading || pageDetailsLoading ? (
        <RoomSkeleton />
      ) : (
        <LiveKitRoom
          serverUrl={room?.serverUrl}
          token={room?.token}
          options={{
            adaptiveStream: true,
            dynacast: true,
          }}
          onConnected={() => {}}
          onError={() => {}}
          audio={
            userChoices?.audioEnabled && {
              deviceId: userChoices?.audioDeviceId,
            }
          }
          video={
            userChoices?.videoEnabled && {
              deviceId: userChoices?.videoDeviceId,
            }
          }
          style={{ width: '100%', height: '100%' }}
        >
          <LayoutContextProvider value={layoutContext}>
            <Box
              position="absolute"
              width="full"
              style={{ inset: '0px' }}
              overflowY="hidden"
              overflowX="hidden"
              height="full"
              border={{
                top: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey200',
                },
              }}
              background="grey100"
            >
              <Box
                flexDirection={isMobile ? 'column' : 'row'}
                flexGrow
                alignItems="center"
                justifyContent="flexStart"
                position="relative"
                style={{ willChange: 'transform' }}
                width="full"
                overflowY="hidden"
                overflowX="hidden"
              >
                <Box
                  paddingX="s24"
                  paddingY="s0"
                  height="full"
                  alignItems="center"
                  justifyContent="center"
                  gap="s4"
                  style={
                    isMobile
                      ? {
                          width: '100%',
                          height: isChatOpen || isSideBarOpen ? '50%' : '100%',
                          transition: 'height 0.5s ease-in-out',
                          willChange: 'height',
                        }
                      : {
                          width:
                            isChatOpen || isSideBarOpen
                              ? `calc(100% - ${SIDEBAR_WIDTH}px)`
                              : '100%',
                          transition: 'width 0.5s ease-in-out',
                          willChange: 'width',
                        }
                  }
                >
                  <RoomHeader />
                  <VideoGrid />
                  <Box
                    paddingTop="s12"
                    paddingBottom={isMobile && (isChatOpen || isSideBarOpen) ? 's0' : 's16'}
                    width="full"
                  >
                    <ControlBar handleDisconnect={handleDisconnect} />
                  </Box>
                </Box>
                <Drawer isMobile={isMobile} isOpen={isChatOpen} sidebarWidth={SIDEBAR_WIDTH}>
                  <Chat />
                </Drawer>
                <Drawer isMobile={isMobile} isOpen={isSideBarOpen} sidebarWidth={SIDEBAR_WIDTH}>
                  <SideBar />
                </Drawer>
              </Box>
            </Box>
            <RoomAudioRenderer />
          </LayoutContextProvider>
        </LiveKitRoom>
      )}
    </Box>
  )

  // Render to main element
  if (typeof document === 'undefined') return portalContent

  const mainElement = document.querySelector('main')
  if (!mainElement) return portalContent

  return createPortal(portalContent, mainElement)
}

const MeetingRoomPage = () => {
  return (
    <RoomProvider>
      <MeetingRoomPageContent />
    </RoomProvider>
  )
}

export default MeetingRoomPage
